import React from 'react';
import {
  Typography,
  Box,
  Chip,
  IconButton,
  Button,
  Sheet,
} from '@mui/joy';
import {
  PencilSimple as EditIcon,
  Trash as DeleteIcon,
  CheckCircle as CheckIcon,
  Clock as ScheduleIcon,
  EnvelopeOpen as DraftIcon,
  XCircle as ErrorIcon,
  Warning as WarningIcon,
  UploadSimple as PublishIcon,
  Image as ImageIcon,
  ArrowUp as ArrowUpwardIcon,
  ArrowDown as ArrowDownwardIcon,
} from '@phosphor-icons/react';
import { format } from 'date-fns';
import type { GBPPost } from '../../types/marketing';
import { ResponsiveTable, type ResponsiveColumn } from '../common/ResponsiveTable';

interface GBPPostsTableProps {
  posts: GBPPost[];
  filtered?: boolean;
  onPreview?: (post: GBPPost) => void;
  onEdit?: (post: GBPPost) => void;
  onDelete?: (post: GBPPost) => void;
  onApprove?: (post: GBPPost) => void;
  onPublish?: (post: GBPPost) => void;
  isPublishing?: number | null;
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
  onSort?: (field: string) => void;
}

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'posted':
      return <CheckIcon />;
    case 'publishing':
    case 'scheduled':
      return <ScheduleIcon />;
    case 'draft':
      return <DraftIcon />;
    case 'failed':
      return <ErrorIcon />;
    case 'pending_review':
      return <WarningIcon />;
    default:
      return null;
  }
};

const getStatusColor = (status: string): any => {
  switch (status) {
    case 'posted':
      return 'success';
    case 'scheduled':
      return 'primary';
    case 'pending_review':
      return 'warning';
    case 'draft':
      return 'neutral';
    case 'failed':
      return 'danger';
    default:
      return 'neutral';
  }
};

const getTypeDisplay = (type: string) => {
  if (!type) return 'Update';
  return type.charAt(0).toUpperCase() + type.slice(1);
};

const formatDate = (dateString?: string | null) => {
  if (!dateString) return '-';
  return format(new Date(dateString), 'MMM d, h:mm a');
};

const truncateContent = (content: string, maxLength: number = 60) => {
  if (content.length <= maxLength) return content;
  return content.substring(0, maxLength) + '...';
};

const PostThumbnail: React.FC<{ post: GBPPost }> = ({ post }) =>
  post.combined_image_url ? (
    <Box sx={{ width: 60, height: 45, borderRadius: 'sm', overflow: 'hidden', flexShrink: 0 }}>
      <img
        src={post.combined_image_url}
        alt={post.title}
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
      />
    </Box>
  ) : (
    <Box
      sx={{
        width: 60,
        height: 45,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.level2',
        borderRadius: 'sm',
        flexShrink: 0,
      }}
    >
      <ImageIcon size={20} color="var(--joy-palette-text-tertiary)" />
    </Box>
  );

const StatusChip: React.FC<{ post: GBPPost }> = ({ post }) => (
  <Chip
    size="sm"
    variant="soft"
    color={getStatusColor(post.status || 'draft')}
    startDecorator={getStatusIcon(post.status || 'draft')}
  >
    {post.status
      ? post.status.replace('_', ' ').charAt(0).toUpperCase() +
        post.status.slice(1).replace('_', ' ')
      : 'Draft'}
  </Chip>
);

export const GBPPostsTable: React.FC<GBPPostsTableProps> = ({
  posts,
  filtered,
  onPreview,
  onEdit,
  onDelete,
  onApprove,
  onPublish,
  isPublishing,
  sortField,
  sortDirection,
  onSort,
}) => {
  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? (
      <ArrowUpwardIcon size={14} />
    ) : (
      <ArrowDownwardIcon size={14} />
    );
  };

  if (posts.length === 0) {
    return (
      <Sheet
        variant="outlined"
        sx={{
          p: 4,
          textAlign: 'center',
          borderRadius: 'sm',
        }}
      >
        <Typography level="body-lg" sx={{ color: 'text.secondary' }}>
          {filtered ? 'No posts match these filters. Clear filters to see all posts.' : 'No posts created yet. Click "Create Post" to get started.'}
        </Typography>
      </Sheet>
    );
  }

  const renderActions = (post: GBPPost) => (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end', maxWidth: {xs: '55vw', md: 'none'} }}>
      {onPreview && <Button size="sm" variant="plain" onClick={() => onPreview(post)}>Preview</Button>}
      {(!post.status || ['draft', 'pending_review', 'scheduled', 'failed'].includes(post.status)) && onPublish && (
        <Button
          size="sm"
          variant="soft"
          color="success"
          startDecorator={<PublishIcon />}
          onClick={() => onPublish(post)}
          loading={isPublishing === post.id}
          disabled={isPublishing != null}
        >
          {post.status === 'failed' ? 'Retry publish' : post.status === 'scheduled' ? 'Publish now' : 'Publish'}
        </Button>
      )}
      {post.status === 'pending_review' && onApprove && (
        <IconButton
          size="sm"
          variant="soft"
          color="success"
          onClick={() => onApprove(post)}
        >
          <CheckIcon />
        </IconButton>
      )}
      {onEdit && post.status !== 'publishing' && (
        <IconButton aria-label={`Edit ${post.title}`} size="sm" variant="outlined" color="primary" onClick={() => onEdit(post)}>
          <EditIcon />
        </IconButton>
      )}
      {onDelete && post.status !== 'publishing' && (
        <IconButton aria-label={`Delete ${post.title}`} size="sm" variant="outlined" color="danger" onClick={() => onDelete(post)}>
          <DeleteIcon />
        </IconButton>
      )}
    </Box>
  );

  const sortableHeader = (label: string, field: string) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        cursor: onSort ? 'pointer' : 'default',
      }}
      onClick={() => onSort?.(field)}
    >
      {label} <SortIcon field={field} />
    </Box>
  );

  const columns: ResponsiveColumn<GBPPost>[] = [
    {
      key: 'post',
      header: 'Post',
      mobileLabel: 'Post',
      mobileVariant: 'body',
      render: (post) => (
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', minWidth: 0 }}>
          {/* On mobile, surface the thumbnail next to the title in the card header */}
          <Box sx={{ display: { xs: 'block', md: 'none' } }}>
            <PostThumbnail post={post} />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography level="title-sm" sx={{ wordBreak: 'break-word' }}>
              {post.title || 'Untitled Post'}
            </Typography>
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              {truncateContent(post.content || '')}
            </Typography>
            {post.status === 'failed' && post.error_message && <Typography level="body-xs" color="danger" sx={{mt:1,overflowWrap:'anywhere'}}>{post.error_message}</Typography>}
          </Box>
        </Box>
      ),
    },
    {
      key: 'image',
      header: 'Image',
      mobileLabel: 'Image',
      width: '80px',
      hideOnMobile: true,
      render: (post) => <PostThumbnail post={post} />,
    },
    {
      key: 'type',
      header: sortableHeader('Type', 'type'),
      mobileLabel: 'Type',
      width: '100px',
      render: (post) => (
        <Chip size="sm" variant="soft" color="neutral">
          {getTypeDisplay(post.type)}
        </Chip>
      ),
    },
    {
      key: 'status',
      header: sortableHeader('Status', 'status'),
      mobileLabel: 'Status',
      mobileVariant: 'header',
      width: '120px',
      render: (post) => <StatusChip post={post} />,
    },
    {
      key: 'scheduled_time',
      header: sortableHeader('Scheduled', 'scheduled_time'),
      mobileLabel: 'Scheduled',
      width: '140px',
      render: (post) => (
        <Typography level="body-sm">{formatDate(post.scheduled_time)}</Typography>
      ),
    },
  ];

  return (
    <ResponsiveTable<GBPPost>
      rows={posts}
      getRowKey={(p) => p.id}
      columns={columns}
      rowActions={onPreview || onEdit || onDelete || onApprove || onPublish ? renderActions : undefined}
      actionsWidth="180px"
    />
  );
};
